import { Future, Perp, Spot, futureSymbolSelectItemFromSecurity } from '@src/components/OMS/NewOrder/models';
import { isSecurityExchangeAndTypeMatch } from '@src/components/OMS/NewOrder/models/utils';
import {
  NewMultilegDefaultDelta1Behaviour,
  type NewMultilegDefaultDelta1Behaviour as NewMultilegDefaultDelta1BehaviourValue,
} from '@src/providers/TradingSettingsContext.types';
import type { CurrencySelectItem, Security, StringSelectItem } from '@talos/kyoko';
import { SecurityTypesUtils } from '@talos/kyoko';
import { MultilegComboType } from '../enums';
import { LegsContainer, type Leg } from '../models';

export interface Delta1Leg2DefaultingInput {
  customLegs: LegsContainer;
  behaviour: NewMultilegDefaultDelta1BehaviourValue;
  getTradableMarketAccountNames?: (security: Security) => string[];
}

/** Delta-1 leg-2 auto-open cell (`MultilegAutoOpenCell` with a fixed `colKey` union). */
export interface Delta1AutoOpenCell {
  rowIndex: number;
  colKey: 'exchange' | 'markets' | 'expiry';
}

export interface Delta1Leg2DefaultingResult {
  customLegs: LegsContainer;
  autoOpenCell?: Delta1AutoOpenCell;
}

type Delta1DefaultingStrategy = (input: Delta1Leg2DefaultingInput, leg1: Perp) => Delta1Leg2DefaultingResult;

const delta1DefaultingStrategies = {
  [NewMultilegDefaultDelta1Behaviour.None]: applyNone,
  [NewMultilegDefaultDelta1Behaviour.PerpVPerp]: applyPerpVPerp,
  [NewMultilegDefaultDelta1Behaviour.PerpVSpot]: applyPerpVSpot,
  [NewMultilegDefaultDelta1Behaviour.PerpVFut]: applyPerpVFut,
} satisfies Record<NewMultilegDefaultDelta1BehaviourValue, Delta1DefaultingStrategy>;

export function applyDelta1Leg2Defaulting(input: Delta1Leg2DefaultingInput): Delta1Leg2DefaultingResult {
  const leg1 = input.customLegs.legs[0];
  if (
    input.customLegs.instrumentType !== MultilegComboType.Delta1Spread ||
    !LegsContainer.isLegPerp(leg1) ||
    !leg1.data.symbolField.value
  ) {
    return { customLegs: input.customLegs };
  }

  return delta1DefaultingStrategies[input.behaviour](input, leg1);
}

function applyNone(input: Delta1Leg2DefaultingInput): Delta1Leg2DefaultingResult {
  return { customLegs: input.customLegs };
}

function applyPerpVPerp(input: Delta1Leg2DefaultingInput, leg1: Perp): Delta1Leg2DefaultingResult {
  const leg2 = Perp.createFromBlank(input.customLegs.referenceData).updateSymbol(leg1.data.symbolField.value);

  return {
    customLegs: setLeg2(input.customLegs, leg2),
    autoOpenCell: { rowIndex: 1, colKey: 'exchange' },
  };
}

function applyPerpVSpot(input: Delta1Leg2DefaultingInput, leg1: Perp): Delta1Leg2DefaultingResult {
  const leg2 = copyValidMarketAccounts(
    input.customLegs,
    Spot.createFromBlank(input.customLegs.referenceData).updateSymbol(getSpotSymbolForLeg(input.customLegs, leg1)),
    undefined,
    input.getTradableMarketAccountNames
  );
  const customLegs = setLeg2(input.customLegs, leg2);

  return {
    customLegs,
    autoOpenCell: customLegs.getMarketAccountsForLeg(1).length === 0 ? { rowIndex: 1, colKey: 'markets' } : undefined,
  };
}

function applyPerpVFut(input: Delta1Leg2DefaultingInput, leg1: Perp): Delta1Leg2DefaultingResult {
  const { future, futureSecurity } = createFutureLegForPerp(input.customLegs, leg1);
  const leg2 = copyValidMarketAccounts(
    input.customLegs,
    future,
    futureSecurity,
    input.getTradableMarketAccountNames
  ).clearExpiry();

  return {
    customLegs: setLeg2(input.customLegs, leg2),
    autoOpenCell: { rowIndex: 1, colKey: 'expiry' },
  };
}

function setLeg2(customLegs: LegsContainer, leg2: Leg): LegsContainer {
  const newLegs = [...customLegs.legs];
  newLegs[1] = leg2;
  return customLegs.setLegs(newLegs);
}

function getSpotSymbolForLeg(customLegs: LegsContainer, leg: Perp): CurrencySelectItem | undefined {
  const symbol = leg.data.symbolField.value;
  if (!symbol) {
    return undefined;
  }
  return Spot.getAvailableSymbols(customLegs.referenceData).find(
    item => item.baseCurrency === symbol.baseCurrency && item.quoteCurrency === symbol.quoteCurrency
  );
}

function createFutureLegForPerp(
  customLegs: LegsContainer,
  leg: Perp
): {
  future: Future;
  futureSecurity?: Security;
} {
  let future = Future.createFromBlank(customLegs.referenceData).updateExchangeAndType(
    leg.data.exchangeAndTypeField.value
  );
  const futureSecurity = getFutureSecurityForLeg(customLegs, leg, future);
  const futureSymbol = futureSecurity ? futureSymbolSelectItemFromSecurity(futureSecurity) : undefined;
  if (futureSymbol) {
    future = future.updateSymbol(futureSymbol);
  }
  return { future, futureSecurity };
}

function getFutureSecurityForLeg(customLegs: LegsContainer, leg: Perp, future: Future): Security | undefined {
  const symbol = leg.data.symbolField.value;
  if (!symbol) {
    return undefined;
  }

  const exchangeAndType = future.data.exchangeAndTypeField.value?.value;
  if (!exchangeAndType) {
    return undefined;
  }

  const futureSecurity = customLegs.referenceData.securities.futures.find(
    security =>
      SecurityTypesUtils.Future.isType(security) &&
      security.BaseCurrency === symbol.baseCurrency &&
      security.QuoteCurrency === symbol.quoteCurrency &&
      isSecurityExchangeAndTypeMatch(security, exchangeAndType, customLegs.referenceData.markets.marketsByName) &&
      future.data.symbolField.availableItems.some(
        item => item.value === futureSymbolSelectItemFromSecurity(security)?.value
      )
  );
  return futureSecurity;
}

function copyValidMarketAccounts<TLeg extends NonNullable<Leg>>(
  customLegs: LegsContainer,
  targetLeg: TLeg,
  targetSecurity?: Security,
  getTradableMarketAccountNames?: (security: Security) => string[]
): TLeg {
  const security = targetSecurity ?? targetLeg.security;
  if (!security || !getTradableMarketAccountNames) {
    return targetLeg;
  }

  const tradableAccounts = new Set(getTradableMarketAccountNames(security));
  const validAccounts: StringSelectItem[] = customLegs
    .getMarketAccountsForLeg(0)
    .filter(account => account.value && tradableAccounts.has(account.value));
  return targetLeg.updateMarketAccount(validAccounts) as TLeg;
}
